/**
 * DataTable — the list primitive every module uses.
 *
 * Column-driven and generic: a page supplies typed columns and rows, and the
 * table owns the chrome (header, hairlines, hover, row click, sticky header,
 * skeleton, empty state). Fixed-height usage fills its flex parent, since the
 * app shell owns scrolling.
 *
 * `.c9-touch-row` pads cells to ~48px on coarse pointers so adjacent hit targets
 * cannot overlap.
 */
import { For, Show, type JSX } from "solid-js";
import { cn } from "./utils/cn";
import { focusRingInset } from "./utils/focus-ring";
import { Skeleton } from "./skeleton";
import { EmptyState } from "./empty-state";

export interface Column<T> {
  /** Stable key; also used as the cell's React-style identity. */
  id: string;
  header: string;
  /** Cell renderer. Keep formatting in the page, not the component. */
  cell: (row: T) => JSX.Element;
  /** Tailwind width/alignment classes for both header and cell. */
  class?: string;
  /** Right-align numeric columns. */
  numeric?: boolean;
}

export function DataTable<T>(props: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  /** Highlights the currently open row when a detail panel is showing. */
  activeKey?: string;
  emptyTitle?: string;
  emptyBody?: string;
  emptyAction?: JSX.Element;
  class?: string;
}): JSX.Element {
  const interactive = () => typeof props.onRowClick === "function";

  return (
    <div class={cn("min-w-0 overflow-x-auto", props.class)}>
      <Show
        when={props.rows.length > 0}
        fallback={
          <EmptyState
            title={props.emptyTitle ?? "Nothing here yet"}
            body={props.emptyBody}
            action={props.emptyAction}
          />
        }
      >
        <table class="w-full min-w-full border-collapse text-body">
          <thead>
            <tr class="border-b border-[color:var(--c9-border)]">
              <For each={props.columns}>
                {(col) => (
                  <th
                    scope="col"
                    class={cn(
                      "whitespace-nowrap px-150 py-100 text-left align-middle",
                      "text-body-s font-[700] uppercase tracking-[0.04em] text-[color:var(--c9-text-subtlest)]",
                      col.numeric && "text-right",
                      col.class,
                    )}
                  >
                    {col.header}
                  </th>
                )}
              </For>
            </tr>
          </thead>
          <tbody>
            <For each={props.rows}>
              {(row) => {
                const key = props.rowKey(row);
                return (
                  <tr
                    tabindex={interactive() ? 0 : undefined}
                    role={interactive() ? "button" : undefined}
                    onClick={() => props.onRowClick?.(row)}
                    onKeyDown={(e) => {
                      if (!interactive()) return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        props.onRowClick?.(row);
                      }
                    }}
                    class={cn(
                      "c9-touch-row border-b border-[color:var(--c9-border)] transition-colors",
                      interactive() && "cursor-pointer hover:bg-[var(--c9-bg-neutral-subtle-hovered)]",
                      props.activeKey === key && "bg-[var(--c9-bg-selected)]",
                      focusRingInset,
                    )}
                  >
                    <For each={props.columns}>
                      {(col) => (
                        <td
                          class={cn(
                            "px-150 py-100 align-middle text-[color:var(--c9-text)]",
                            col.numeric && "text-right tabular-nums",
                            col.class,
                          )}
                        >
                          {col.cell(row)}
                        </td>
                      )}
                    </For>
                  </tr>
                );
              }}
            </For>
          </tbody>
        </table>
      </Show>
    </div>
  );
}

/** Reserves the exact row height/grid of the loaded table — no layout jump. */
DataTable.Skeleton = function DataTableSkeleton(props: {
  columns: number;
  rows?: number;
  class?: string;
}): JSX.Element {
  return (
    <div class={cn("min-w-0", props.class)}>
      <div class="border-b border-[color:var(--c9-border)] px-150 py-100">
        <Skeleton class="h-4 w-32" />
      </div>
      <For each={Array.from({ length: props.rows ?? 8 })}>
        {() => (
          <div class="flex items-center gap-200 border-b border-[color:var(--c9-border)] px-150 py-150">
            <For each={Array.from({ length: props.columns })}>
              {(_, i) => <Skeleton class={cn("h-4", i() === 0 ? "w-40" : "w-24")} />}
            </For>
          </div>
        )}
      </For>
    </div>
  );
};
